import {identity, pickBy} from 'lodash-es';
import {useCallback, useEffect, useMemo, useState} from 'react';

import type {CurrencyFilters} from '@/services/currencyService';
import {currencyService} from '@/services/currencyService';
import type {AfriexCurrency} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {CurrenciesListProps} from './types';

const PAGE_SIZE = 10;

const useCurrencyUpdate = (): CurrenciesListProps => {
  const [fullList, setFullList] = useState<AfriexCurrency[]>([]);
  const [item, setItem] = useState<AfriexCurrency>({} as AfriexCurrency);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState(null);
  const [shouldShowForm, setShouldShowForm] = useState<boolean>(false);
  const [filters, setFilters] = useState<CurrencyFilters>(
    {} as CurrencyFilters,
  );

  // The whole catalogue is already in memory (the loop below), so a typed
  // search term filters it directly rather than round-tripping to a server
  // that has no name/code query param to begin with.
  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return fullList;
    }
    return fullList.filter(
      (currency) =>
        currency.name?.toLowerCase().includes(term) ||
        currency.code?.toLowerCase().includes(term),
    );
  }, [fullList, searchTerm]);

  const isSearch = Boolean(searchTerm.trim());

  const list = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const fetchList = useCallback(
    async (params: CurrencyFilters) => {
      try {
        setIsLoading(true);
        // Downloading the whole collection is deliberate here: there is one row
        // per currency (`code` is limited to AfriexCurrencyCode plus the crypto
        // symbols, well under 150), so this loop runs once or twice and the
        // screen can then search, sort and page the full list locally.
        //
        // Do not copy this loop for a collection that grows (transactions,
        // fees, users, audit events). Every page re-runs the server's count
        // and a longer skip, so each page costs more than the last, and the
        // browser ends up holding every row. For those, page on the server and
        // render one page at a time, stream the full range from one cursor
        // (the `?action=DOWNLOAD` exports built on streamCursorToConsumer), or,
        // in a server job, read in `_id` batches the way
        // BatchTransactionProcessor does.
        const allItemsMap = new Map<string, AfriexCurrency>();
        let currentPageIndex = 0;
        let hasMore = true;
        let itemsTotal = 0;

        while (hasMore) {
          const response = await currencyService.getList({
            ...params,
            ...filters,
            page: currentPageIndex,
            limit: 100,
          });

          const items = response.data || [];
          itemsTotal = response.total || 0;

          items.forEach((item: AfriexCurrency) => {
            //deduplicate by ID or code as a fallback because of the 2 currencies being returned always
            const key = item.id || item.code;
            allItemsMap.set(key, item);
          });

          //stop if we have fetched all items, or if the current page is empty/incomplete
          if (
            allItemsMap.size >= itemsTotal ||
            items.length === 0 ||
            items.length < 100
          ) {
            hasMore = false;
          } else {
            currentPageIndex++;
          }
        }

        const sortedList = Array.from(allItemsMap.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setFullList(sortedList);
        setPage(0); //reset to first page on fresh fetch
      } catch (e) {
        showErrorMessage(
          `Fetching currency list failed:${getErrorMessage(e, String(e))}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  const fetchItem = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const item = await currencyService.getItem(id);
      setItem(item);
    } catch (e) {
      showErrorMessage(
        `Fetching currency details failed:${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleFilters = useCallback((filters: CurrencyFilters) => {
    // remove undefined values
    const cleanedFilters = pickBy(filters, identity);

    setFilters({page: 0, ...cleanedFilters});
  }, []);

  useEffect(() => {
    // `fetchList` try/catches into showErrorMessage, so this only fires if that
    // reporting itself threw. Attached so an empty currency list cannot end up
    // looking like a loaded one; dev logger rather than a second toast, since a
    // toast is what just failed.
    fetchList({}).catch(logger.error);
  }, [fetchList]);

  const handleUpdate = useCallback(
    async (currencyId: string, body: Partial<AfriexCurrency>) => {
      try {
        if (!body) {
          return;
        }

        setError(null);
        setIsLoading(true);
        const currency = await currencyService.saveOne(currencyId, body);
        if (currency) {
          showSuccessMessage('Currency updated successfully');
          // Update local state with the API response
          setItem(currency);
          // Refresh the list to reflect updates. Awaited so `setIsLoading(false)`
          // below only runs once the list actually shows the saved currency;
          // `fetchList` reports into showErrorMessage rather than throwing, so
          // this cannot divert into the catch and mislabel the failure.
          await fetchList({});
        } else {
          // The endpoint responds 200 with an empty body when the `_id`
          // filter matches no currency (stale id, deleted currency), so a
          // resolved-but-falsy response means nothing was saved.
          showErrorMessage('Updating currency failed: currency not found');
        }
      } catch (e) {
        showErrorMessage(
          `Updating currency failed:${getErrorMessage(e, String(e))}`,
        );
      }
      setIsLoading(false);
    },
    [fetchList],
  );

  // Filters the already-loaded `fullList` in place, rather than the previous
  // implementation which re-hit the server with `status: searchTerm` (the
  // currency endpoint has no such filter, so that call never matched anything
  // and this box was never wired to the UI to begin with).
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(0);
  }, []);

  const toggleForm = useCallback(() => {
    setShouldShowForm(!shouldShowForm);
  }, [shouldShowForm]);

  return {
    error,
    isLoading,
    isSearch,
    handleUpdate,
    searchTerm,
    handleSearch,
    handleFilters,
    fetchList,
    pageInfo: {
      page,
      total: filteredList.length,
    },
    setPage,
    list,
    item,
    fetchItem,
    shouldShowForm,
    toggleForm,
  };
};

export default useCurrencyUpdate;
